import { useForm } from 'react-hook-form';
import { addFolderSchema } from '@views/pages/Reporting/Reports/schema';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useAddReportFolderMutation,
  useOrganizationsShortQuery,
} from '@common/cache';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { useState } from 'react';

interface IAddReportFolderFormShape {
  name: string;
  organization_id: string;
  isPrivate: boolean;
}

const initialValues: Partial<IAddReportFolderFormShape> = {
  name: '',
  organization_id: '',
  isPrivate: false,
};

export const useAddReportFolderModal = (handleClose: () => void) => {
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const formMethods = useForm<IAddReportFolderFormShape>({
    mode: 'onChange',
    resolver: yupResolver(addFolderSchema),
    defaultValues: initialValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isValid, isDirty },
  } = formMethods;

  const { data: organizations } = useOrganizationsShortQuery();

  const { mutateAsync, isPending } = useAddReportFolderMutation();

  const handleCloseAction = () => {
    if (isDirty) {
      setIsSaveChangesModalOpen(true);
    } else {
      handleClose();
    }
  };

  const submitHandler = async (values: IAddReportFolderFormShape) => {
    try {
      await mutateAsync({
        name: values.name,
        organization_id: values.organization_id,
        private: values.isPrivate,
      });
      toast.success('Folder successfully created.');
      reset();
      handleClose();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    handleClose();
    reset();
  };

  const handleSaveChanges = async () => {
    await onSubmit();
    reset();
    setIsSaveChangesModalOpen(false);
  };

  return {
    formMethods,
    organizationOptions: (organizations || []).map(o => ({
      value: o.id,
      label: o.name,
    })),
    onSubmit,
    isAddReportFolderPending: isPending,
    isValid,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isEditSubmitting: isPending,
    handleCloseAction,
  };
};
